import { useState, useEffect } from "react";
import AdminLayout from "./layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Users, Plus, Copy, Check, Shield, Loader2, AlertTriangle,
  Building2,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const SPECIALTIES = [
  "rheumatology","dermatology","oncology","endocrinology",
  "gastroenterology","neurology","cardiology","respiratory",
  "nephrology","urology","haematology","ophthalmology",
  "orthopaedics","paediatrics","psychiatry","radiology",
];

interface Team {
  id: number;
  trustName: string;
  specialty: string;
  displayName: string;
  isActive: boolean;
}

export default function AdminTeams() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [trustName, setTrustName] = useState("");
  const [specialty, setSpecialty] = useState("rheumatology");
  const [displayName, setDisplayName] = useState("");
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTeams = () => {
    fetch(`${BASE}/api/admin/teams`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(setTeams)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTeams(); }, []);

  const handleCreate = async () => {
    if (!trustName.trim() || !displayName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/api/admin/teams`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trustName: trustName.trim(), specialty, displayName: displayName.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error ?? "Failed to create team");
      }
      const { team, joinCode: code } = await res.json();
      setJoinCode(code);
      setTeams([...teams, team]);
      setTrustName("");
      setDisplayName("");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <AdminLayout title="Teams">
      <div className="max-w-3xl space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-amber-100 p-2.5 rounded-xl">
            <Users className="h-6 w-6 text-amber-700" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Teams</h2>
            <p className="text-sm text-slate-500">Create teams and generate join codes for Team Leads</p>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-800">
          <p className="font-semibold mb-1">How Team Leads are created:</p>
          <ol className="list-decimal list-inside space-y-1 text-blue-700">
            <li>Create a team below — a <strong>join code</strong> is generated automatically</li>
            <li>Share the code with the Trust's designated Team Lead</li>
            <li>They sign up and enter the code — they <strong>automatically become Team Lead</strong></li>
            <li>The Team Lead can then invite their team members</li>
          </ol>
        </div>

        {/* Create team form */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Create New Team
          </h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">NHS Trust Name</Label>
              <Input
                value={trustName}
                onChange={e => setTrustName(e.target.value)}
                placeholder="e.g. University Hospitals of Derby and Burton"
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Specialty</Label>
              <select
                value={specialty}
                onChange={e => setSpecialty(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {SPECIALTIES.map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Display Name</Label>
              <Input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="e.g. Rheumatology Advice Line — Royal Derby"
                className="text-sm"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          {joinCode && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Team created! Join code:</p>
              <div className="flex items-center justify-between">
                <p className="text-lg font-mono font-bold text-emerald-800 tracking-wider">{joinCode}</p>
                <Button variant="ghost" size="sm" onClick={() => handleCopy(joinCode)}>
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-emerald-600 mt-1">Share this code with the Team Lead. It's single-use for the first person.</p>
            </div>
          )}

          <Button
            onClick={handleCreate}
            disabled={!trustName.trim() || !displayName.trim() || creating}
            className="bg-indigo-600 hover:bg-indigo-700 rounded-full"
          >
            {creating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating...</> : <><Plus className="h-4 w-4 mr-1" /> Create team</>}
          </Button>
        </div>

        {/* Existing teams */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
            <p className="text-sm font-semibold text-slate-700">Existing Teams ({teams.length})</p>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
          ) : teams.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No teams yet. Create one above.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {teams.map(t => (
                <div key={t.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="h-9 w-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <Building2 className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">{t.displayName}</p>
                    <p className="text-xs text-slate-500">{t.trustName} · <span className="capitalize">{t.specialty.replace(/_/g, " ")}</span></p>
                  </div>
                  {t.isActive ? (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">Active</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500">Inactive</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
